import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SectionHeading from "@/components/ui/SectionHeading";
import ScriptText from "@/components/ui/ScriptText";

gsap.registerPlugin(ScrollTrigger);

interface Stage {
  title: string;
  description: string;
}

interface PhilosophyTimelineProps {
  scriptAccent?: string;
  heading: string;
  stages: Stage[];
}

export default function PhilosophyTimeline({
  scriptAccent,
  heading,
  stages,
}: PhilosophyTimelineProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReducedMotion) return;

    const cards = el.querySelectorAll(".timeline-stage");
    cards.forEach((card, i) => {
      gsap.fromTo(
        card,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none none",
          },
          delay: i * 0.05,
        },
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach((st) => {
        const trigger = st.trigger;
        if (trigger && el.contains(trigger)) st.kill();
      });
    };
  }, []);

  return (
    <section ref={ref} className="bg-antique-linen px-6 py-28 lg:px-12 lg:py-44">
      <div className="mx-auto max-w-5xl">
        <SectionHeading scriptAccent={scriptAccent} heading={heading} />

        <div className="mx-auto mt-16 max-w-2xl">
          {stages.map((stage, i) => (
            <div
              key={i}
              className="timeline-stage relative border-l border-warm-stone/30 py-12 pl-10 last:border-l-transparent"
            >
              {/* Dot */}
              <div className="absolute -left-[5px] top-12 h-2.5 w-2.5 rounded-full bg-faded-terracotta" />

              {/* Stage number */}
              <ScriptText className="mb-2 block text-2xl">
                {String(i + 1).padStart(2, "0")}
              </ScriptText>

              <h3 className="mb-3 font-serif text-xl font-light text-soft-charcoal lg:text-2xl">
                {stage.title}
              </h3>
              <p className="font-body text-sm leading-relaxed text-soft-charcoal/70 lg:text-base">
                {stage.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
